import { Pill, type PillSize, type PillVariant } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { ShiftWindow } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";

interface WindowedOutPillProps {
  size?: PillSize;
  variant?: PillVariant;
  window: string;
  workerScore?: number;
  windowThreshold?: number;
  disabled?: boolean;
}

export function WindowedOutPill(props: WindowedOutPillProps) {
  const {
    size = "small",
    variant = "filled",
    window,
    workerScore,
    windowThreshold,
    disabled = true,
  } = props;

  const label = useMemo(() => {
    switch (window) {
      case ShiftWindow.A_TEAM: {
        return "Priority Plus";
      }

      case ShiftWindow.CLIPBOARD_SCORE_FIRST:
      case ShiftWindow.CLIPBOARD_SCORE_SECOND: {
        return "High Scorers";
      }

      case ShiftWindow.RELIABLE_AND_FAMILIAR: {
        // If worker passes score threshold, we can infer it was a familiarity issue and show "Regulars"
        if (
          isDefined(workerScore) &&
          isDefined(windowThreshold) &&
          workerScore >= windowThreshold
        ) {
          return "Regulars";
        }

        // Otherwise, we can infer it was a score issue and show "High Scorers"
        return "High Scorers";
      }

      case ShiftWindow.TIERS: {
        return "Gold";
      }

      default: {
        return "Unavailable";
      }
    }
  }, [window, workerScore, windowThreshold]);

  function formatPill(label: string, variant: PillVariant, size: PillSize): React.ReactNode {
    if (label === "Gold") {
      return (
        <Pill
          variant="outlined"
          size={size}
          label={label}
          iconType="locked-filled"
          backgroundColor={(theme) => theme.palette.background.tertiary}
          color={(theme) => theme.palette.tiers?.gold.text}
          iconColor={(theme) => theme.palette.tiers?.gold.text}
          outlineColor={(theme) => theme.palette.tiers?.gold.text}
          sx={{
            width: "fit-content",
            position: "relative",
            zIndex: 3,
          }}
        />
      );
    }

    return (
      <Pill
        variant={variant}
        size={size}
        label={label}
        iconType="hourglass"
        backgroundColor={(theme) => theme.palette.intent?.disabled.backgroundInvert}
        color={(theme) => theme.palette.text.secondary}
        iconColor={(theme) => theme.palette.text.secondary}
        sx={{
          width: "fit-content",
          position: "relative",
          zIndex: 3,
        }}
      />
    );
  }

  return (
    <>
      {formatPill(label, variant, size)}
      {/* This is to give the perception that the containing element is disabled. Not ideal, but it works. */}
      {disabled && (
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        />
      )}
    </>
  );
}
